/**
 * Shared setup for run, exec, and headless commands
 *
 * Extracts the env-var construction and the core exec call shared by run.ts, exec.ts, exec-batch.ts and headless.ts.
 */

import type { ProxyMode } from '@spacecode/shared/remote';
import { resolveAuthToken } from '../config.js';
import { getGitHubToken } from '../sync/config.js';
import { buildEnvVars } from './env.js';

export interface RunSetupResult {
  envVars: Record<string, string>;
}

/**
 * Build the env vars for a core exec task.
 *
 * Shared by `run`, `exec`, and `headless` commands so the setup logic
 * lives in exactly one place.
 */
export async function setupRun(options?: { proxyMode?: ProxyMode }): Promise<RunSetupResult> {
  const ghToken = await getGitHubToken();
  const envVars = buildEnvVars({ ghToken, proxyMode: options?.proxyMode });
  return { envVars };
}

/** Options the sandbox single loop honored; the core ignores them. Warn instead of silently dropping what the user typed. */
export function warnIgnoredOptions(options: { noCommit?: boolean; idleTimeout?: number; verbose?: boolean }): void {
  const ignored = [
    options.noCommit ? '--no-commit' : null,
    options.idleTimeout !== undefined ? '--idle-timeout' : null,
    options.verbose ? '--verbose' : null,
  ].filter((o): o is string => o !== null);
  if (ignored.length > 0) process.stderr.write(`warning: ${ignored.join(', ')} ha${ignored.length === 1 ? 's' : 've'} no effect: tasks now run through the core.\n`);
}

const GITHUB_TOKEN = /^(gh[pousr]_[A-Za-z0-9]{20,255}|github_pat_[A-Za-z0-9_]{20,255})$/;

export interface ExecParams {
  repo: string;
  prompt: string;
  branch?: string;
  envVars?: Record<string, string>;
}

/**
 * Runs a task through the core (`POST /api/core/exec`, spec 2026-10-21-core-headless-routing-design.md). The route answers when the
 * run is done; every failure, including "not enabled for this account" (403/404), throws.
 */
export async function coreExec(params: ExecParams, signal?: AbortSignal) {
  const { DEFAULT_API_URL } = await import('@spacecode/shared/remote');
  const { loadConfig } = await import('../config.js');
  const { execViaCore } = await import('./core-exec.js');
  const config = await loadConfig();
  const gh = params.envVars?.GH_TOKEN;
  return execViaCore({
    baseUrl: process.env.SPACECODE_API_URL ?? config.apiUrl ?? DEFAULT_API_URL,
    token: await resolveAuthToken(),
    repo: params.repo,
    prompt: params.prompt,
    ...(params.branch ? { branch: params.branch } : {}),
    ...(gh && GITHUB_TOKEN.test(gh) ? { ghToken: gh } : {}),
    ...(signal ? { signal } : {}),
  });
}
